/**
 * Transcription transactions — the troubleshooting view. Every batch job, recorded clip and live
 * mic session lands here with its correlation id, so a third-party report can be traced end to end.
 *
 * Follows the CoOrdio list-page pattern established by {@link ProjectListPage}: `ShellHeader`,
 * `CordioAbpPagedDataTable`, and a BEM block named after the page slug. There is no create action
 * and no `DeleteDialog`: transactions are created by running a job (the playground, or the public
 * `POST /v1/audio/transcriptions` API) and the API has no delete. Audio upload and playback stay
 * where they were — the playground and the transaction detail page — so this page keeps only the
 * pointer to them, in the header.
 *
 * ── COLUMN PLAN (field → header → width → cell type → filter) ──────────────────────────────────
 *   createdAt          → Created     → 16% → text via valueGetter, locale date + time → popover date `fromUtc`/`toUtc`
 *   mode               → Mode        → 13% → custom Chip (outlined, MODE_LABELS)      → popover select `mode`
 *   status             → Status      → 12% → custom Chip (API's own status value)     → popover select `status`
 *   fileName           → Audio       → 21% → custom (file name + language · size)     → toolbar search
 *   agentSlug          → Agent       → 14% → custom Chip, em dash when unscoped       → toolbar search
 *   correlationId      → Correlation → 24% → custom (short id + client id chip)       → popover text `correlationId`
 *                                            (widths sum to 100%)
 *
 * DROPPED from the old MUI table: nothing. The per-row "copy correlation id" button and the "open
 * transcript & event log" button moved out of their cells into declarative row actions, which is
 * where the grid renders and aligns them.
 *
 * Two things the grid cannot express, kept outside it:
 *   • `?correlationId=…` deep links. `CordioAbpPagedDataTable` has no `urlSync` (only the FHIR
 *     table does), so a filter cannot be seeded from the query string. The value is held in page
 *     state instead, shown as a dismissible chip in the toolbar, and applied when the popover's own
 *     correlation filter is empty. Typing in the popover overrides it.
 *   • The old empty state's copy ("run a job from the playground, or call POST …"). The grid owns
 *     its empty state and the ABP table does not expose its `actionSlot`, so that guidance became
 *     the header's description and its "Open Playground" action.
 *
 * No column is `sortable`: `GET /transcriptions` sorts newest-first and takes no `sortBy`.
 */

import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  Button,
  Chip,
  CordioAbpPagedDataTable,
  ShellHeader,
  type ChipColor,
  type CordioDataTableAction,
  type CordioDataTableColumn,
  type CordioDataTableListToolbarConfig,
} from '@coordio/ui';
import { EmptyState } from '@coordio/ui/layout';
import { Mic } from 'lucide-react';

import {
  createCortexPagedDataSource,
  type CortexPagedQuery,
} from '../components/data-display/cortexPagedDataSource';
import { transcriptionService } from '../services/transcriptionService';
import { useContextStore } from '../store/contextStore';
import { useNotificationStore } from '../store/notificationStore';
import { ROUTES } from '../utils/routes';

import type { TranscriptionListFilters, TranscriptionTransactionDto } from '../types/transcription';

import './TranscriptionsPage.scss';

/** `TranscriptionStatus`, as the API spells it — these are also the values the `status` filter takes. */
const STATUS_COLORS: Record<string, ChipColor> = {
  Completed: 'green',
  Failed: 'red',
  Processing: 'blue',
  Pending: 'amber',
  Cancelled: 'default',
};

/** `TranscriptionMode` → the product's wording for it. Values are what the `mode` filter takes. */
const MODE_LABELS: Record<string, string> = {
  BatchFile: 'Batch file',
  BatchLiveRecording: 'Recorded clip',
  RealtimeFile: 'Realtime file',
  RealtimeMic: 'Live mic',
};

const GUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Jobs land seconds apart, so the created column keeps the time the old page showed. */
function formatCreatedAt(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

function asFilterString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

/**
 * Narrows the grid's query to exactly the keys `transcriptionService.listTransactions` accepts.
 *
 * An explicit whitelist rather than a spread, so a future grid filter cannot leak a parameter the
 * endpoint would ignore.
 *
 * The correlation value is split the way the old page split it: a GUID is the platform's own
 * `correlationId`, anything else is the caller's `clientCorrelationId`. `linkedCorrelation` is the
 * `?correlationId=` deep link, used only when the popover filter is empty.
 */
function toTranscriptionFilters(
  query: CortexPagedQuery,
  linkedCorrelation: string | null,
): TranscriptionListFilters {
  const correlation = asFilterString(query.correlationId) ?? asFilterString(linkedCorrelation);
  const isGuid = correlation ? GUID_PATTERN.test(correlation) : false;

  return {
    pageNumber: query.pageNumber,
    pageSize: query.pageSize,
    searchTerm: query.searchTerm,
    status: asFilterString(query.status),
    mode: asFilterString(query.mode),
    fromUtc: asFilterString(query.fromUtc),
    toUtc: asFilterString(query.toUtc),
    correlationId: isGuid ? correlation : undefined,
    clientCorrelationId: !isGuid ? correlation : undefined,
  };
}

export const TranscriptionsPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { addNotification } = useNotificationStore();

  const currentTenant = useContextStore((s) => s.currentTenant);
  const currentProject = useContextStore((s) => s.currentProject);
  const hasContext = Boolean(currentTenant && currentProject);

  /**
   * The `?correlationId=` deep link, which the grid cannot seed itself. Initialised once from the
   * query string; clearing it changes `dataSource`'s identity, which is how the grid is told to
   * refetch without it.
   */
  const [linkedCorrelation, setLinkedCorrelation] = useState<string | null>(
    () => searchParams.get('correlationId')?.trim() || null,
  );

  const dataSource = useMemo(
    () =>
      createCortexPagedDataSource<TranscriptionTransactionDto>((query) => {
        // Unreachable while the guard below renders instead of the grid; throwing rather than
        // silently resolving empty keeps a future regression visible.
        if (!hasContext) throw new Error('No workspace or project selected');
        return transcriptionService.listTransactions(toTranscriptionFilters(query, linkedCorrelation));
      }),
    // The tenant/project ids scope the request server-side (via the API client's context headers),
    // so they belong in the deps even though they are not passed as arguments.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hasContext, linkedCorrelation, currentTenant?.id, currentProject?.id],
  );

  const columns = useMemo<ReadonlyArray<CordioDataTableColumn<TranscriptionTransactionDto>>>(
    () => [
      {
        field: 'createdAt',
        header: 'Created',
        type: 'text',
        width: '16%',
        valueGetter: (row) => formatCreatedAt(row.createdAt),
      },
      {
        field: 'mode',
        header: 'Mode',
        type: 'custom',
        width: '13%',
        customCell: (row) => <Chip label={MODE_LABELS[row.mode] ?? row.mode} variant="outlined" />,
      },
      {
        field: 'status',
        header: 'Status',
        type: 'custom',
        width: '12%',
        // The label is the API's own status value, so a status the UI has not heard of still reads
        // correctly (in the neutral colour).
        customCell: (row) => <Chip label={row.status} color={STATUS_COLORS[row.status] ?? 'default'} />,
      },
      {
        field: 'fileName',
        header: 'Audio',
        type: 'custom',
        width: '21%',
        customCell: (row) => (
          <span className="transcriptions__audio">
            <span className="transcriptions__audio-name">{row.fileName ?? 'Streamed audio'}</span>
            <span className="transcriptions__audio-meta">
              {row.language}
              {row.audioSizeBytes ? ` · ${Math.round(row.audioSizeBytes / 1024)} KB` : ''}
            </span>
          </span>
        ),
      },
      {
        field: 'agentSlug',
        header: 'Agent',
        type: 'custom',
        width: '14%',
        customCell: (row) =>
          row.agentSlug ? (
            <Chip label={row.agentSlug} color="accent" variant="outlined" />
          ) : (
            <span className="transcriptions__muted">—</span>
          ),
      },
      {
        field: 'correlationId',
        header: 'Correlation',
        type: 'custom',
        width: '24%',
        customCell: (row) => (
          <span className="transcriptions__correlation">
            <code className="transcriptions__correlation-id">{row.correlationId.substring(0, 8)}…</code>
            {row.clientCorrelationId && <Chip label={row.clientCorrelationId} variant="outlined" />}
          </span>
        ),
      },
    ],
    [],
  );

  const clearLinkedCorrelation = useCallback(() => setLinkedCorrelation(null), []);

  const listToolbar = useMemo<CordioDataTableListToolbarConfig>(
    () => ({
      search: { placeholder: 'File name, transcript, agent slug, error, request id' },
      // Every one of these is a parameter `GET /transcriptions` accepts. `status` is a five-value
      // dimension, so it is a select rather than three quick tabs.
      filters: [
        {
          key: 'status',
          label: 'Status',
          type: 'select',
          placeholder: 'Any status',
          options: Object.keys(STATUS_COLORS).map((status) => ({ label: status, value: status })),
        },
        {
          key: 'mode',
          label: 'Mode',
          type: 'select',
          placeholder: 'Any mode',
          options: Object.entries(MODE_LABELS).map(([value, label]) => ({ label, value })),
        },
        {
          key: 'correlationId',
          label: 'Correlation id',
          type: 'text',
          placeholder: 'GUID or your own trace value',
          description: 'A GUID matches the platform correlation id; anything else matches your own.',
        },
        { key: 'fromUtc', label: 'Created from', type: 'date' },
        { key: 'toUtc', label: 'Created to', type: 'date' },
      ],
      filterButtonLabel: 'Filter',
      trailingContent: linkedCorrelation ? (
        <Chip
          label={`Linked correlation ${linkedCorrelation.substring(0, 8)}… ✕`}
          color="accent"
          onClick={clearLinkedCorrelation}
        />
      ) : undefined,
    }),
    [linkedCorrelation, clearLinkedCorrelation],
  );

  const copyCorrelationId = useCallback(
    async (row: TranscriptionTransactionDto) => {
      try {
        await navigator.clipboard.writeText(row.correlationId);
        addNotification('Correlation id copied to clipboard', 'success');
      } catch {
        addNotification('Failed to copy to clipboard', 'error');
      }
    },
    [addNotification],
  );

  const actions = useMemo<ReadonlyArray<CordioDataTableAction<TranscriptionTransactionDto>>>(
    () => [
      {
        icon: 'copy',
        tooltip: 'Copy correlation id',
        onClick: (row) => void copyCorrelationId(row),
      },
      {
        icon: 'eye',
        tooltip: 'Open transcript & event log',
        onClick: (row) => navigate(ROUTES.TRANSCRIPTION_DETAIL.replace(':id', row.id)),
      },
    ],
    [copyCorrelationId, navigate],
  );

  if (!hasContext) {
    return (
      <div className="transcriptions" data-testid="transcription-list-page">
        <ShellHeader
          title="Transcriptions"
          description="Every speech-to-text job with its correlation id and event timeline."
          data-testid="transcription-list-header"
        />
        <EmptyState
          title="No workspace or project selected"
          description="Choose a workspace and a project from the switchers above to see their transcription transactions."
          data-testid="transcription-empty-state"
        />
      </div>
    );
  }

  return (
    <div className="transcriptions" data-testid="transcription-list-page">
      <ShellHeader
        title="Transcriptions"
        description="Every speech-to-text job with its correlation id and event timeline. Jobs arrive from the playground or from POST /v1/audio/transcriptions with a project API key."
        data-testid="transcription-list-header"
        actions={
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="gap-2"
            onClick={() => navigate(ROUTES.TRANSCRIPTION_PLAYGROUND)}
          >
            <Mic className="h-4 w-4 shrink-0" aria-hidden />
            Open Playground
          </Button>
        }
      />

      <div data-testid="transcription-list-grid">
        <CordioAbpPagedDataTable<TranscriptionTransactionDto>
          className="transcriptions__table"
          columns={columns}
          dataSource={dataSource}
          initialPageSize={25}
          maxBodyHeight="520px"
          listToolbar={listToolbar}
          actions={actions}
          onRowClick={(row) => navigate(ROUTES.TRANSCRIPTION_DETAIL.replace(':id', row.id))}
        />
      </div>
    </div>
  );
};
